import { useState, useEffect, useContext } from "react";
import moment from "moment";

import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";

import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIGrid from "@mui/material/Grid";

import Dialog from "../DialogFunctional";
import MainContext from "../../Context/MainContext/MainContext";
import { ChartContext } from "../../Context/ChartContext/ChartContext";
import OrderStatusReason from "../../Utility/Crud/OrderStatusReason";
import Patient from "../../Utility/Crud/Patient";
import api from "../../Seating/Security/api";

export default function DialogCancelReopenTicket({ open, onClose }) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const orderActivity =
    chartContext.originalChart.orderActivity[chartContext.orderId];
  const activity = chartContext.originalChart.activity[chartContext.orderId];
  const patient = chartContext.originalChart.patient[chartContext.patientId];
  const [error, setError] = useState({});
  const [cancelNoteText, setCancelNoteText] = useState("");
  const [cancelledOrderStatusReason, setCancelledOrderStatusReason] =
    useState(null);
  const [pendingSave, setPendingSave] = useState(false);

  /**
   * Returns the dynamic label for the order/ticket/service type.
   * @returns {string}
   */
  function getTypeLabel() {
    return `${orderActivity.orderType} ${activity.type}`;
  }

  /**
   * Determines which dialog data to show based on order status.
   * @returns {Object} Dialog data object.
   */
  function getDialogData() {
    const isCancelled = orderActivity.status === 4;
    return isCancelled ? getReopenDialogData() : getCancelDialogData();
  }

  /**
   * Builds dialog data for cancelling a ticket.
   * @returns {Object} Dialog data object for cancellation.
   */
  function getCancelDialogData() {
    return {
      title: `Cancel ${getTypeLabel()}`,
      description: `Are you sure you want to cancel this ${getTypeLabel()}?`,
      content: [
        <MUIGrid key="reason" item xs={12} md={6}>
          {renderReasonsForCancellation()}
        </MUIGrid>,
        <MUIGrid key="note" item xs={12}>
          {renderAddCancelNoteField()}
        </MUIGrid>,
      ],
      actions: {
        cancelText: `Do not cancel`,
        confirmText: `Cancel ${getTypeLabel()}`,
        onClick: () => handleCancelConfirm(),
      },
    };
  }

  /**
   * Builds dialog data for reopening a ticket.
   * @returns {Object} Dialog data object for reopening.
   */
  function getReopenDialogData() {
    const reopenWindowDays =
      orderActivity?.orderType === "Billing Issue" ? 90 : 30;
    const lastCancellationOlderThanReopenWindow = moment(
      getLastCancellationTimestamp(chartContext.chart.auditLog),
    ).isSameOrBefore(moment().subtract(reopenWindowDays, "days"), "day");

    const description = lastCancellationOlderThanReopenWindow
      ? `This ${getTypeLabel()} cannot be reopened because it was canceled more than ${reopenWindowDays} days ago. Please create a new ${getTypeLabel()} instead.`
      : `Are you sure you want to reopen this ${getTypeLabel()}?`;

    const actions = lastCancellationOlderThanReopenWindow
      ? {
          cancelText: null,
          confirmText: "Ok",
          onClick: () => onClose(),
        }
      : {
          cancelText: `Do not reopen`,
          confirmText: `Reopen ${getTypeLabel().toLowerCase()}`,
          onClick: () => handleReopenConfirm(),
        };

    return {
      title: `Reopen ${getTypeLabel()}`,
      description,
      content: [],
      actions,
    };
  }

  useEffect(() => {
    setCancelledOrderStatusReason(null);
    setError({});
    setCancelNoteText("");
  }, [onClose]);

  /**
   * Handles confirmation for cancelling a ticket.
   */
  function handleCancelConfirm() {
    if (
      cancelledOrderStatusReason &&
      cancelNoteText &&
      cancelNoteText.trim().length > 0
    ) {
      cancelTicket();
      onClose();
    } else if (cancelledOrderStatusReason) {
      setError((prev) => ({ ...prev, cancelledOrderStatusReason: false }));
    } else if (cancelNoteText && cancelNoteText.trim().length > 0) {
      setError((prev) => ({ ...prev, cancelNoteText: false }));
    }
    if (!cancelledOrderStatusReason) {
      setError((prev) => ({ ...prev, cancelledOrderStatusReason: true }));
    }
    if (!cancelNoteText || cancelNoteText.trim().length === 0) {
      setError((prev) => ({ ...prev, cancelNoteText: true }));
    }
  }

  /**
   * Handles confirmation for reopening a ticket.
   */
  function handleReopenConfirm() {
    reopenTicket();
    onClose();
  }

  function getLastCancellationTimestamp(auditLog) {
    let timestampOfLastCancellation = null;

    if (!auditLog) {
      return null;
    }

    const sortedAudits = Object.values(auditLog)
      .filter(
        (audit) =>
          audit.rowId == orderActivity.id &&
          audit.columnName === "order_status_reason_id",
      )
      .sort((a, b) => {
        const timestampDiff =
          moment.utc(b.timestamp).valueOf() - moment.utc(a.timestamp).valueOf();
        if (timestampDiff !== 0) {
          return timestampDiff;
        }
        return b.id - a.id;
      });

    for (const audit of sortedAudits) {
      const newOrderStatusReason =
        chartContext.chart?.orderStatusReason?.[audit?.newValue] || null;
      if (newOrderStatusReason?.orderStatus === 4) {
        timestampOfLastCancellation = audit.timestamp;
        break;
      }
    }
    return timestampOfLastCancellation;
  }

  /**
   * Gets the last non-cancelled order status reason ID from the audit log.
   * @param {Object} auditLog - The audit log object.
   * @returns {number|null} Previous order status reason ID or null.
   */
  function getLastNonCancelledOrderStatusReasonId(auditLog) {
    let previousOrderStatusReasonId = null;

    if (!auditLog) {
      return null;
    }
    // Sort by oldest timestamp first (id tiebreaker), and keep the latest non-cancel oldValue seen.
    Object.values(auditLog)
      .filter(
        (audit) =>
          audit.rowId == orderActivity.id &&
          audit.columnName === "order_status_reason_id",
      )
      .sort((a, b) => {
        const timestampDiff =
          moment.utc(a.timestamp).valueOf() - moment.utc(b.timestamp).valueOf();
        if (timestampDiff !== 0) {
          return timestampDiff;
        }
        return a.id - b.id;
      })
      .forEach((audit) => {
        const oldOrderStatusReason =
          mainContext.orderStatusReasonsIndexed?.[audit?.oldValue] || null;
        if (oldOrderStatusReason?.orderStatus !== 4) {
          previousOrderStatusReasonId = audit?.oldValue;
        }
      });

    return previousOrderStatusReasonId;
  }

  /**
   * Renders the autocomplete field for reasons for cancellation.
   * @returns {JSX.Element}
   */
  const renderReasonsForCancellation = () => {
    const orderStatusReasons = OrderStatusReason.sort(
      Object.values(mainContext.orderStatusReasonsIndexed).filter(
        (orderStatusReason) => orderStatusReason.orderStatus === 4,
      ),
    );

    return (
      <MUIAutocomplete
        options={orderStatusReasons}
        value={cancelledOrderStatusReason}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionKey={(option) => option?.id || ""}
        getOptionLabel={(option) => option?.description || ""}
        onChange={(e, newValue) => {
          setCancelledOrderStatusReason(newValue);
        }}
        renderInput={(params) => (
          <MUITextField
            {...params}
            InputLabelProps={{ shrink: true }}
            required={true}
            label="Reason for Cancellation"
            error={error.cancelledOrderStatusReason ? true : false}
            helperText={
              error.cancelledOrderStatusReason ? "Reason is required." : ""
            }
            size="small"
            variant="standard"
            fullWidth
            sx={{ mb: 2 }}
          />
        )}
      />
    );
  };
  /**
   * Renders the add note field for creating a new note.
   * @returns {JSX.Element}
   */
  const renderAddCancelNoteField = () => {
    const handleCancelNoteTextChange = (updatedFields) => {
      setCancelNoteText(updatedFields.cancelNoteText);
    };

    return (
      <MUITextField
        autoFocus={true}
        multiline
        rows={4}
        fullWidth={true}
        required={true}
        label="Cancellation Note"
        InputLabelProps={{ shrink: true }}
        inputProps={{ style: { resize: "both" } }}
        onChange={(e) =>
          handleCancelNoteTextChange({ cancelNoteText: e.target.value })
        }
        error={error.cancelNoteText ? true : false}
        helperText={error.cancelNoteText ? "Note is required." : ""}
        variant="standard"
        sx={{ mb: 2 }}
      />
    );
  };

  // Cancel Tickets
  /**
   * Cancels the ticket and creates a cancellation note.
   * @returns {Promise<void>}
   */
  const cancelTicket = async () => {
    chartContext.resetChart();
    chartContext.onChange({
      orderActivity: {
        [orderActivity.id]: {
          orderStatusReasonId: cancelledOrderStatusReason.id,
          status: cancelledOrderStatusReason.orderStatus,
        },
      },
    });
    setPendingSave(true);
    if (chartContext.orderId && cancelNoteText.trim().length > 0) {
      await api.send("POST", `/activities/${chartContext.orderId}/notes`, {
        accountId: patient.accountId,
        activityId: chartContext.orderId,
        assignedToId: null,
        completed: true,
        createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
        dueDate: null,
        isAutoVerification: false,
        patientName: Patient.getDisplayName(patient),
        task: false,
        text: cancelNoteText,
        type: "ActivityNote",
      });
    }
    setCancelNoteText("");
    setCancelledOrderStatusReason(null);
    setError({});
    return;
  };

  useEffect(() => {
    if (pendingSave) {
      chartContext.handleSaveChart();
      setPendingSave(false);
    }
  }, [chartContext.chart, pendingSave]);

  /**
   * Reopens the ticket by restoring previous status.
   * @returns {Promise<void>}
   */
  const reopenTicket = async () => {
    chartContext.resetChart();
    const lastNonCancelledOrderStatusReasonId =
      getLastNonCancelledOrderStatusReasonId(chartContext.chart.auditLog);
    const previousStatus =
      mainContext.orderStatusesIndexed[
        mainContext.orderStatusReasonsIndexed[
          parseInt(lastNonCancelledOrderStatusReasonId)
        ]?.orderStatus
      ]?.id;
    const previousOrderStatusReasonId = lastNonCancelledOrderStatusReasonId;

    chartContext.onChange({
      orderActivity: {
        [chartContext.orderId]: {
          status: previousStatus,
          orderStatusReasonId: previousOrderStatusReasonId,
        },
      },
    });
    setPendingSave(true);
    return;
  };

  /**
   * Render header title of dialog
   */
  function renderHeader() {
    const dialogData = getDialogData();
    return (
      <MUITypography variant="h6" color="textPrimary">
        {dialogData.title}
      </MUITypography>
    );
  }

  /**
   * Render main content text of dialog
   */
  function renderContent() {
    const dialogData = getDialogData();
    return (
      <>
        <MUITypography sx={{ mb: 2 }} variant="body1">
          {dialogData.description}
        </MUITypography>
        <MUIGrid container spacing={2} rowSpacing={1}>
          {dialogData.content}
        </MUIGrid>
      </>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  function renderActions() {
    const dialogData = getDialogData();
    const { cancelText, confirmText, onClick } = dialogData.actions;
    return (
      <MUIDialogActions spacing={2}>
        {cancelText && (
          <MUIButton variant="text" onClick={onClose}>
            {cancelText}
          </MUIButton>
        )}
        <MUILoadingButton variant="contained" onClick={onClick}>
          {confirmText || "Confirm"}
        </MUILoadingButton>
      </MUIDialogActions>
    );
  }

  /**
   * Gets the max width of the dialog.
   * @returns {string}
   */
  function getMaxWidth() {
    return "md";
  }

  return (
    <Dialog
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={renderActions}
      maxWidth={getMaxWidth}
      open={open}
      onClose={onClose}
    />
  );
}
